import { type FC, useEffect, useMemo, useRef } from 'react'
import { useSubmitEnrollmentHomeData } from '@egw/api'
import { GenericError } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { useRouter } from '@egw/router'
import { type FeatureFlags, WEALTH_ROUTES } from '@egw/constants'
import { useAuth } from '@egw/auth'
import { EnrollmentLayout } from '@egw/layouts'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useFlags } from '@egw/feature-flags'
import { enrollmentCookieService } from '@egw/enrollment-cookie'
import { riskService } from '@egw/risk-service'
import { ROUTES } from '../../constants/routes/routes'
import { ROUTES as GENERIC_ROUTES } from '../../constants/routes/routes-generic'
import { ROUTES as MULTI_ROUTES } from '../../constants/routes/routes-multi'

export const HomePage: FC = () => {
  const { navigate } = useRouter()
  const { isLoading: isAuthLoading, isAuthenticated } = useAuth()
  const { t } = useTranslation('features-enrollment')
  const hasExecutedRef = useRef(false)
  const { flags } = useFlags<FeatureFlags>()

  const initialPayload = useMemo(() => {
    return {
      initiateApplication: true,
    }
  }, [])

  const { mutate, isLoading, error } = useSubmitEnrollmentHomeData({
    onSuccess: (res) => {
      if (res.__typename === 'SubmitEnrollmentSuccess' && res.path) {
        void navigate(res.path)
      } else {
        void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${GENERIC_ROUTES.ERROR.path}`)
      }
    },
    onError: () => {
      void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${GENERIC_ROUTES.ERROR.path}`)
    },
  })

  // Navigate to Enrollment Experience
  useEffect(() => {
    const handleEnrollment = async (): Promise<void> => {
      // Prevent multiple executions
      if (hasExecutedRef.current || isAuthLoading) {
        return
      }

      hasExecutedRef.current = true

      if (isAuthenticated) {
        if (flags.rolloutJointEnrollmentV1) {
          navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES.INVESTMENTS_ACCOUNT_OWNERSHIP.path}`)
        } else {
          // Reset enrollment session before initiating application
          await enrollmentCookieService.resetSession()
          await riskService.updateFlow('enrollment')
          // initiating application and follow the path provided by BE -  should show Diesel Enrollment
          void mutate({ initiateApplication: true })
        }
      } else {
        if (flags.rolloutJointEnrollmentV1) {
          navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${GENERIC_ROUTES.SELECT_ACCOUNT.path}`) // indirectly - multi or joint decision
        } else {
          navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${MULTI_ROUTES.MULTI_HOME.path}`) // directly - multi
        }
      }
    }

    void handleEnrollment()
  }, [isAuthLoading, isAuthenticated, navigate, initialPayload, flags.rolloutJointEnrollmentV1])

  return (
    <EnrollmentLayout title="Enrollment">
      <div className="egw-container-enroll h-full flex flex-col">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}
      </div>

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentLayout>
  )
}
